import React, { useCallback } from 'react'
import { Checkbox } from '@you/ui'
import { Icon } from '@/components/Icon'
import type { DepartmentTreeNode } from '@/api/types/organization.type'

// 根据字符串生成随机颜色的函数
const getRandomColor = (str: string): string => {
  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FFEAA7',
    '#DDA0DD',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E9',
    '#F8C471',
    '#82E0AA',
    '#F1948A',
    '#85C1E9',
    '#D7BDE2',
  ]
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}
interface TreeNodeProps {
  node: DepartmentTreeNode
  level: number
  selectedIds: Set<string>
  expandedIds: Set<string>
  loadingOrgs: Set<string>
  onToggleExpand: (id: string) => void
  onToggleSelect: (id: string, checked: boolean) => void
  getChildSelectedCount: (
    node: DepartmentTreeNode,
    currentSelectedIds: Set<string>
  ) => number
  getChildCount: (node: DepartmentTreeNode) => number
  fetchDepartmentTree?: (teamId: string) => void
  isLinkageRef: React.MutableRefObject<boolean>
}

// TreeNode组件 - 暂时移除React.memo确保展开收起功能正常工作
const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  level,
  selectedIds,
  expandedIds,
  loadingOrgs,
  onToggleExpand,
  onToggleSelect,
  getChildSelectedCount,
  getChildCount,
  fetchDepartmentTree,
  isLinkageRef,
}) => {
  const isExpanded = expandedIds.has(node.id)
  const isSelected = selectedIds.has(node.id)
  const hasChildren = node.subDepartments && node.subDepartments.length > 0
  const isRoot = node.isRoot
  const isLoading = loadingOrgs.has(node.id)
  const childSelectedCount = getChildSelectedCount(node, selectedIds)
  const childCount = getChildCount(node)

  // 移除调试信息，保持代码简洁

  // 处理展开/收起 - 立即响应，不依赖异步操作
  const handleExpandToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      // 立即切换展开状态
      onToggleExpand(node.id)

      // 如果是根节点且没有子部门，异步加载部门树
      if (isRoot && !hasChildren && fetchDepartmentTree) {
        fetchDepartmentTree(node.id)
      }
    },
    [isRoot, hasChildren, fetchDepartmentTree, node.id, onToggleExpand]
  )

  // 处理选择 - 立即响应
  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation()
      onToggleSelect(node.id, e.target.checked)
    },
    [node.id, onToggleSelect]
  )

  const paddingLeft = level * 20

  return (
    <div>
      <div
        className="flex items-center px-3 py-2 rounded-lg transition-all duration-200 hover:bg-[#F5F4FE]"
        style={{
          backgroundColor: '#F5F4FE78',
          borderRadius: '8px',
          height: '42px',
          marginBottom: '4px',
          paddingLeft: `${paddingLeft + 12}px`,
        }}
      >
        {/* 多选框 - 专门处理选择 */}
        <div className="mr-3">
          <Checkbox
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="data-[state=checked]:bg-[#8752FF] data-[state=checked]:border-[#8752FF]"
          />
        </div>

        {/* 展开/收起箭头 - 专门处理展开收起 */}
        {(hasChildren || isRoot) && (
          <div
            className="w-4 h-4 mr-2 flex items-center justify-center cursor-pointer"
            onClick={handleExpandToggle}
          >
            <Icon
              name="arrowRight"
              className={`w-4 h-4 transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              }`}
            />
          </div>
        )}

        {/* 组织/部门图标 */}
        {isRoot ? (
          <div
            className="w-6 h-6 mr-2 rounded-full flex items-center justify-center text-white text-xs font-bold"
            style={{
              backgroundColor: getRandomColor(node.name),
            }}
          >
            {node.name.charAt(0)}
          </div>
        ) : (
          <Icon name="department" size={16} className="mr-2" />
        )}

        {/* 部门名称 */}
        <span
          className="flex-1 font-bold"
          style={{
            color: '#2A0F67',
            fontSize: '16px',
            fontWeight: 700,
          }}
        >
          {node.name}
        </span>
        {/* 加载状态 */}
        {isLoading && (
          <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-[#8752FF] border-t-transparent"></div>
        )}

        {/* 子部门数量提示 */}
        {hasChildren && (
          <span className="text-xs text-gray-500 ml-2">
            ({childSelectedCount}/{childCount})
          </span>
        )}
      </div>

      {/* 子部门 */}
      {hasChildren && (
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="ml-4">
            {node.subDepartments!.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                level={level + 1}
                selectedIds={selectedIds}
                expandedIds={expandedIds}
                loadingOrgs={loadingOrgs}
                onToggleExpand={onToggleExpand}
                onToggleSelect={onToggleSelect}
                getChildSelectedCount={getChildSelectedCount}
                getChildCount={getChildCount}
                fetchDepartmentTree={fetchDepartmentTree}
                isLinkageRef={isLinkageRef}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default TreeNode
